const post = {
  slug: 'narrow-youtube-niche',
  cluster: 'niches',
  eyebrow: 'Niches',
  title: 'Why a narrow niche beats a broad one',
  description:
    'Narrowing feels like giving up audience. It is the opposite — and there are three concrete mechanisms behind why.',
  keywords: [
    'narrow youtube niche', 'niche down youtube', 'specific vs broad niche',
    'youtube channel focus', 'micro niche youtube', 'youtube positioning',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/narrow-youtube-niche.webp',
    alt: 'A wide funnel with scattered arrows beside a narrow one where every arrow lands in the same place.',
  },
  lede:
    'Going narrow feels like turning down audience, which is why people resist it. The mechanism is the reverse: a narrow channel is *easier* to get watched, and the reasons are specific rather than motivational.',
  sections: [
    {
      heading: 'What narrow actually means',
      body: [
        {
          type: 'para',
          text: 'Not a smaller subject. A specific *kind of video*.',
        },
        {
          type: 'example',
          badLabel: 'Broad',
          goodLabel: 'Narrow',
          bad: 'A channel about business.',
          good: 'A channel about companies that were market leaders and no longer exist, one company per video.',
          note: 'The second one tells you what every future video looks like. That is the property doing the work.',
        },
        {
          type: 'para',
          text: 'The test: can a stranger predict what your next video will be like, without predicting its topic? If yes, you are narrow enough.',
        },
      ],
    },
    {
      heading: 'Mechanism one: recommendation needs something to match',
      body: [
        {
          type: 'para',
          text: 'Recommendation works by finding people who liked things like this. A channel where every video is a similar treatment gives the system a consistent pattern to match against, and each successful video makes the next one easier to place.',
        },
        {
          type: 'para',
          text: 'A channel where video one is a company post-mortem, video two is a productivity list and video three is a news roundup gives it three unrelated signals. Each video starts from scratch.',
        },
      ],
    },
    {
      heading: 'Mechanism two: research compounds',
      body: [
        {
          type: 'para',
          text: 'This is the practical benefit nobody mentions and it is large.',
        },
        {
          type: 'para',
          text: 'In a narrow niche, the fifth video is faster to make than the first. You already know the sources, the recurring names, the standard objections, the terms practitioners use. Context carries over.',
        },
        {
          type: 'para',
          text: 'In a broad niche it never carries over. Every video is a cold start, which means either the research stays shallow or the cadence collapses. Usually both.',
        },
      ],
    },
    {
      heading: 'Mechanism three: you can be the best at something',
      body: [
        {
          type: 'para',
          text: 'You cannot be the best channel about history. You can plausibly be the best channel about a specific category of engineering failure, because roughly nobody is trying.',
        },
        {
          type: 'para',
          text: 'That matters because "best available video on this exact question" is a reachable position, and it is what gets a video recommended over an equally competent one from a bigger channel.',
        },
        {
          type: 'quote',
          text: 'Narrow and deep travels further than broad and shallow — and it is the only one of the two a new channel can actually execute.',
        },
      ],
    },
    {
      heading: 'The fear, and whether it is real',
      body: [
        {
          type: 'para',
          text: 'The objection is always the same: I will run out of topics.',
        },
        {
          type: 'para',
          text: 'In practice this is rare. Channels overwhelmingly die from being indistinguishable, not from exhausting a subject. And a narrow niche that genuinely runs dry after fifty videos has, by then, given you an audience and a reputation you can widen from — which is a far better position than the one broadness starts you in.',
        },
        {
          type: 'para',
          text: 'The honest failure case exists though: a niche so narrow it has eight topics. The twenty-topic test in [choosing a niche](/blogs/faceless-youtube-niches) is what catches it.',
        },
      ],
    },
    {
      heading: 'Widening later',
      body: [
        {
          type: 'para',
          text: 'The right sequence is narrow first, widen from strength. Once a channel is known for one thing, adjacent material carries: people came for the specific thing and will follow you slightly outward.',
        },
        {
          type: 'para',
          text: 'The reverse — starting broad and hoping to find focus — does not work, because there is no established thing for the widening to extend from.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How narrow is too narrow?',
      a: 'When you cannot list twenty distinct videos. That is the operative limit, and it is a much lower bar than most people assume — a well-chosen narrow niche usually generates more topics than expected once you are inside it.',
    },
    {
      q: 'Does a narrow niche limit how big the channel can get?',
      a: 'Eventually, yes — every subject has a ceiling. That constraint binds much later than the one that actually kills channels, which is not being distinguishable enough to get watched at all.',
    },
  ],
  related: ['faceless-youtube-niches', 'how-to-evaluate-a-youtube-niche', 'specific-script-better-visuals'],
};

export default post;
